/** @format **/
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import { useDispatch, useSelector } from 'react-redux';
import { useDiscountsHook, useDiscountSimpleSelectElements } from '../../../modules/lib/hooks/useDiscountHook';
import GridItem from '../../../components/Grid/GridItem';
import { ProductActionField, ProductActions } from '../../../modules/simcard/ProductReducer';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from '@material-ui/core';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { AlertDialog } from '../../lib/dialog/AlertDialog';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import SimCardInterface from '../../../interfaces/platform/SimCardInterface';
import { DiscountCollectionInterface } from '../../../interfaces/api/DiscountCollectionInterface';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { DateHelper } from '../../../helpers/DateHelper';
import Pager from '../../../components/Adescom/PagerRange';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import SimCard from '@material-ui/icons/SimCard';
import CardBody from '../../../components/Card/CardBody';
import Card from '../../../components/Card/Card';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import PaperedTable from '../../../components/Table/PaperedTable';
import { DISCOUNT_CONSTANTS } from '../../../modules/discounts/reducers';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import GeneralDialog from '../../../components/Dialog/GeneralDialog';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import DatePickerForm from '../../../components/Form/DatePickerForm';
import CustomDatePicker from '../../../components/Form/CustomDatePicker';
import { Moment } from 'moment';
import { DiscountHelper } from '../../../helpers/DiscountHelper';
import ActivateFeatureButton from '../../../components/Buttons/ActivateFeatureButton';
import I18n from '../../../components/Translate/I18n';
import { DiscountInterface } from '../../../interfaces/billing/DiscountInterface';

type ProductDiscountType = {
    platformResellerId: number;
    simCard: SimCardInterface;
};

export const ProductDiscount: FunctionComponent<ProductDiscountType> = ({ platformResellerId, simCard }): ReactElement | null => {
    const setDefaultFilters = (): Array<Filter> => [
        { name: 'simCardId', value: simCard.id },
        { name: 'discountAll', value: 1 },
    ];
    const setDefaultOrders = (): Array<Order> => [{ name: 'id', value: 'desc' }];

    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();
    const [discounts, setDiscounts] = useState<DiscountCollectionInterface | null>();
    const [discountToActivate, setDiscountToActivate] = useState(-1);
    const [discountNameToActivate, setDiscountNameToActivate] = useState('');
    const [alertDialog, setAlertDialog] = useState(false);
    const [productDiscountList, setProductDiscountList] = useState<DiscountCollectionInterface | null>(null);
    const [filters, setFilters] = useState<Array<Filter>>(setDefaultFilters);
    const [orders, setOrders] = useState<Array<Order>>(setDefaultOrders);
    const [discountUsage, setDiscountUsage] = useState<any>(null);
    const [discountManageOperation, setDiscountManageOperation] = useState<{ discountId: number; operationType: number } | null>(null);
    const [discountEndDate, setDiscountEndDate] = useState<Moment | string | null>(null);

    const discountSimpleList = useDiscountSimpleSelectElements(discounts);

    const discountSelect = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        setDiscountToActivate(parseInt(event.target.value));
        for (let i = 0; i < discountSimpleList.length; i++) {
            if (discountSimpleList[i].value === parseInt(event.target.value)) {
                setDiscountNameToActivate(discountSimpleList[i].name);
            }
        }
    };

    const activateDiscountRequest = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getUrl('/operation_on_sim_cards/' + simCard.id),
            ProductActions.PRODUCT_PUT_ACTIVATE_DISCOUNT,
            { id: simCard.id, activationType: 5, discountId: discountToActivate },
            (): void => {
                setAlertDialog(false);
                setDiscountToActivate(-1);
                setDiscountNameToActivate('');
                setProductDiscountList(null);

                loadDiscountUsage(simCard);
            },
            { dispatchAfterCallback: false, propagateDataToCallback: true }
        );
    };

    useEffect(() => {
        if (productDiscountList === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discounts')),
                'discount_product_list_first_load',
                filters,
                orders,
                (data: DiscountCollectionInterface) => {
                    setProductDiscountList(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, productDiscountList, filters, orders, simCard]);

    const paginationCallbackFunction = (uri: string): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.paginationCallWithCallback(
            uri,
            'discount_product_list_pagination',
            (data: DiscountCollectionInterface): void => {
                setProductDiscountList(data);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const loadDiscountUsage = (simCard: any): void => {
        if (simCard) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discount_usage_on_products')),
                'discount_usage_on_products',
                [{ name: 'simCardId', value: simCard.id }],
                [],
                (data: any) => {
                    setDiscountUsage(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    };

    const setEndDateOnDiscountConfirm = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discount_manages')),
            'discount_manages',
            {
                discountId: discountManageOperation?.discountId,
                dateEnd: discountEndDate,
                operationType: DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS.DISCOUNT_PRODUCT_OPERATION_SET_END,
            },
            (data: any) => {
                setProductDiscountList(null);
                setDiscountManageOperation(null);
                setDiscountEndDate(null);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const cancelSetEndDateOnDiscount = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discount_manages')),
            'discount_manages',
            {
                discountId: discountManageOperation?.discountId,
                operationType: DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS.DISCOUNT_PRODUCT_OPERATION_CANCEL_SET_END,
            },
            (data: any) => {
                setProductDiscountList(null);
                setDiscountManageOperation(null);
                setDiscountEndDate(null);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const discountManageDispatcher = (): void => {
        if (discountManageOperation) {
            switch (discountManageOperation?.operationType) {
                case DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS.DISCOUNT_PRODUCT_OPERATION_SET_END:
                    setEndDateOnDiscountConfirm();
                    break;
                case DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS.DISCOUNT_PRODUCT_OPERATION_CANCEL_SET_END:
                    cancelSetEndDateOnDiscount();
                    break;
                default:
                    break;
            }
        }
    };

    const setEndDateOnDiscountCancel = (): void => {
        setDiscountManageOperation(null);
        setDiscountEndDate(null);
    };

    const discountManageOperationHandler = (params: { discountId: number; operationType: number }): void => {
        setDiscountManageOperation(params);
    };
    const checkPackageOneTime = (discount: DiscountInterface): boolean => {
        if (discount) {
            let discountParameters = null;
            if (discount.discountParamPartnerReseller && typeof discount.discountParamPartnerReseller === 'string') {
                discountParameters = JSON.parse(discount.discountParamPartnerReseller);
            } else {
                discountParameters = discount.discountParamPartnerReseller;
            }

            if (discountParameters && discountParameters.oneTime) {
                return true;
            }
        }

        return false;
    };

    useEffect(() => {
        if (simCard) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discount_usage_on_products')),
                'discount_usage_on_products',
                [{ name: 'simCardId', value: simCard.id }],
                [],
                (data: any) => {
                    setDiscountUsage(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, simCard]);

    useEffect((): void => {
        if (!discounts) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/discounts')),
                DISCOUNT_CONSTANTS.LIST,
                [
                    { name: 'platformResellerId', value: platformResellerId },
                    { name: 'endDateNullable', value: DateHelperTs.getCurentDate() },
                    { name: 'allowUseOnProduct', value: 1 },
                    { name: 'discountAll', value: 1 },
                    { name: 'active', value: true },
                    { name: 'itemsPerPage', value: 9999999 },
                ],
                [{ value: 'asc', name: 'mainname' }],
                (data: DiscountCollectionInterface | null) => {
                    setDiscounts(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    });

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    if (discounts || productDiscountList) {
        return (
            <Card>
                <CardHeader icon={<SimCard />} trans="discount:h.discount" />
                <CardBody>
                    {discounts && discounts['hydra:member'].length > 0 && (
                        <GridContainer>
                            <GridItem xs={4}>
                                {!UserRoleHelper.checkRole(roles.ROLE_SUBSCRIPTION_VIEW_ONLY) && !simCard.isPrepaid && (
                                    <SimpleSelect
                                        label="discount:h.allowed_discount_on_product"
                                        required={false}
                                        elements={discountSimpleList}
                                        dispatchName={'no-name'}
                                        fieldName="discountSelect"
                                        callback={discountSelect}
                                        selectedValue={discountToActivate}
                                    />
                                )}
                                {discountToActivate !== -1 && (
                                    <CustomTextButtom
                                        callback={(): void => setAlertDialog(true)}
                                        callbackParam={{}}
                                        label="discount:h.activate_discount_on_product"
                                        size="sm"
                                    />
                                )}
                                <AlertDialog
                                    open={alertDialog}
                                    titleDialog="discount:h.activate_discount_on_product"
                                    displayMessage="discount:h.activate_discount_on_product_confirm_message"
                                    displayMessageValueObject={{ promotion: discountNameToActivate }}
                                    closeCallback={(): void => {
                                        setDiscountToActivate(-1), setAlertDialog(false);
                                    }}
                                    confirmCallback={activateDiscountRequest}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                    <GridContainer>
                        <GridItem xs={12} style={{ marginTop: '15px', overflow: 'auto' }}>
                            <PaperedTable>
                                <TableHead>
                                    <TableRow>
                                        <TableCell classes={tableStickyHeader} padding="none" align="left">
                                            <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="center">
                                            <TableHeaderContentCell
                                                textAlign="center"
                                                title="discount:h.name"
                                                updateOrderList={updateOrderList}
                                                updateFilterList={updateFilterList}
                                                fieldName="mainname"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType="string"
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="center">
                                            <TableHeaderContentCell
                                                textAlign="center"
                                                title="discount:h.date_start"
                                                updateOrderList={updateOrderList}
                                                updateFilterList={updateFilterList}
                                                fieldName="dateStart"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType="date"
                                                timeFormat={false}
                                                dateFormat="RRRR-MM-DD"
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="center">
                                            <TableHeaderContentCell
                                                textAlign="center"
                                                title="discount:h.date_end"
                                                updateOrderList={updateOrderList}
                                                updateFilterList={updateFilterList}
                                                fieldName="dateEnd"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType="date"
                                                timeFormat={false}
                                                dateFormat="RRRR-MM-DD"
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="center">
                                            <TableHeaderContentCell textAlign="center" title="discount:h.initial_value" />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="center">
                                            <TableHeaderContentCell textAlign="center" title="discount:h.remain" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productDiscountList &&
                                        productDiscountList['hydra:member'].map((discount) => {
                                            let usageContent = (
                                                <>
                                                    <TableCell classes={tableCellRoot}></TableCell>
                                                    <TableCell classes={tableCellRoot}></TableCell>
                                                </>
                                            );

                                            const isPackageOneTime = checkPackageOneTime(discount);

                                            discountUsage &&
                                                discountUsage['hydra:member'].map((usage: any) => {
                                                    if (discount.id === usage.discountId) {
                                                        usageContent = (
                                                            <>
                                                                <TableCell classes={tableCellRoot}>{usage.initialValue}</TableCell>
                                                                <TableCell classes={tableCellRoot}>{usage.usage}</TableCell>
                                                            </>
                                                        );
                                                    }
                                                });
                                            return (
                                                <>
                                                    <TableRow key={'discount_' + discount.id}>
                                                        <TableCell classes={tableCellRoot}>
                                                            {!discount.overwriteDateEnd && !isPackageOneTime && !discount.belongToPackageGroup && (
                                                                <DeleteButton
                                                                    tooltipTitle={'discount:b.set_end_date_on_discount'}
                                                                    callback={discountManageOperationHandler}
                                                                    callbackParam={{
                                                                        discountId: discount.id,
                                                                        operationType:
                                                                            DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS
                                                                                .DISCOUNT_PRODUCT_OPERATION_SET_END,
                                                                    }}
                                                                />
                                                            )}
                                                            {discount.overwriteDateEnd && !isPackageOneTime && (
                                                                <ActivateFeatureButton
                                                                    tooltipTitle={'discount:b.cancel_set_end_date_on_discount'}
                                                                    callback={discountManageOperationHandler}
                                                                    callbackParam={{
                                                                        discountId: discount.id,
                                                                        operationType:
                                                                            DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS
                                                                                .DISCOUNT_PRODUCT_OPERATION_CANCEL_SET_END,
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>

                                                        <TableCell classes={tableCellRoot}>{discount.mainname}</TableCell>
                                                        <TableCell classes={tableCellRoot} align="center">
                                                            {DateHelper.formatDDMMYYYY(discount.dateStart)}
                                                        </TableCell>
                                                        <TableCell classes={tableCellRoot} align="center">
                                                            {DateHelper.formatDDMMYYYY(discount.dateEnd)}
                                                        </TableCell>
                                                        {usageContent}
                                                    </TableRow>
                                                </>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={10}>
                                            {productDiscountList && productDiscountList['hydra:view'] && (
                                                <Pager callback={paginationCallbackFunction} views={productDiscountList} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </PaperedTable>
                        </GridItem>
                    </GridContainer>
                    <GeneralDialog
                        title={'discount:h.promotion_set_end_date_title'}
                        dialogType={'action'}
                        open={!!discountManageOperation?.discountId}
                        agreeCallback={discountManageDispatcher}
                        disagreeCallback={setEndDateOnDiscountCancel}
                        agreeCallbackTextLabel={'general:common.confirm'}
                        disagreeCallbackTextLabel={'general:common.cancel'}
                    >
                        {discountManageOperation &&
                            discountManageOperation.operationType ===
                                DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS.DISCOUNT_PRODUCT_OPERATION_SET_END && (
                                <div style={{ height: 400 }}>
                                    <CustomDatePicker
                                        helpText={'discount:h.discount_end_date'}
                                        fieldName={'discountEndDate'}
                                        callback={(moment: Moment): void => setDiscountEndDate(moment.add(2, 'h'))}
                                        dateFormat={'DD-MM-YYYY'}
                                        placeholder={'DD-MM-YYYY'}
                                        autoComplete={'off'}
                                        timeFormat={false}
                                        defaultValue={discountEndDate}
                                    />
                                </div>
                            )}
                        {discountManageOperation &&
                            discountManageOperation.operationType ===
                                DiscountHelper.DISCOUNT_PRODUCT_OPERATIONS.DISCOUNT_PRODUCT_OPERATION_CANCEL_SET_END && (
                                <I18n trans={'discount:discount_manage.confirmation_cancel_set'} />
                            )}
                    </GeneralDialog>
                </CardBody>
            </Card>
        );
    }
    return null;
};
